import { getFundReturnsRange, type FundReturnsRange, type SimpleFund, type SubjectRangeAnalysis } from 'venn-api';
import { createFundReturnsRangeKey, useQuery } from 'venn-utils';
import { getReturnsRangeFromError } from './utils';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import type { FundToProxy } from '../types';
import { type LayeredDetailedProxyMetadata, getFirstProxy } from '../../../hooks/useProxyDataQuery';

export function useInvestmentsReturnsRanges(
  investments: (FundToProxy | SimpleFund)[],
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>,
  rangeDataByFund: Record<string, SubjectRangeAnalysis | undefined>,
): FundReturnsRange[] {
  const unproxiedReturnsRanges: FundReturnsRange[] = useMemo(() => {
    return investments.map((investment) => {
      const proxyData = getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const));
      const rangeData = rangeDataByFund[investment.id];
      return {
        start: proxyData ? proxyData.unproxiedStartDate : rangeData!.start,
        end: proxyData ? proxyData.unproxiedEndDate : rangeData!.end,
        frequency: proxyData ? proxyData.unproxiedFrequency : rangeData!.frequency,
      };
    });
  }, [investments, proxyDataByFund, rangeDataByFund]);

  return unproxiedReturnsRanges;
}

/** Returns data as null if the proxyId is undefined. */
export function useProxyReturnsRange(selectedProxyId: string | undefined) {
  const proxySetId = useRecoilValue(proxySetIdState);
  return useQuery(createFundReturnsRangeKey(selectedProxyId ?? '-'), async ({ signal }) =>
    selectedProxyId ? fetchFundReturnsRange(selectedProxyId, proxySetId, signal) : null,
  );
}

function fetchFundReturnsRange(id: string, proxySetId: string | undefined, signal: AbortSignal | undefined) {
  return getFundReturnsRange(true, id, proxySetId, signal)
    .then((res) => res.content)
    .catch((e) => getReturnsRangeFromError(e) ?? null);
}
